import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Shield, Zap, BarChart3 } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface PlansHeaderProps {
  className?: string
}

const TRUST_ICON_KEYS = [
  { icon: Shield, key: 'publicPlans.hero.trust1' },
  { icon: Zap, key: 'publicPlans.hero.trust2' },
  { icon: BarChart3, key: 'publicPlans.hero.trust3' },
] as const

export const PlansHeader: React.FC<PlansHeaderProps> = ({ className }) => {
  const { t } = useTranslation()

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-3xl',
        'bg-gradient-to-br from-primary-900 via-primary-700 to-primary-600',
        'px-6 py-12 sm:px-10 sm:py-16 lg:px-16 lg:py-20',
        'text-center',
        className,
      )}
    >
      {/* Decorative elements */}
      <div
        aria-hidden
        className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl pointer-events-none"
        style={{ backgroundColor: 'hsl(var(--secondary) / 0.12)' }}
      />
      <div
        aria-hidden
        className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full blur-3xl pointer-events-none"
        style={{ backgroundColor: 'hsl(var(--accent) / 0.08)' }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(hsl(var(--primary-foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary-foreground)) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative mx-auto max-w-3xl space-y-6">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-secondary">
          {t('publicPlans.hero.eyebrow')}
        </p>

        <h1 className="text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl lg:text-5xl leading-tight">
          {t('publicPlans.hero.title')}
        </h1>

        <p className="mx-auto max-w-2xl text-base leading-7 text-primary-200 sm:text-lg">
          {t('publicPlans.hero.subtitle')}
        </p>

        {/* Trust signals */}
        <div className="flex flex-wrap items-center justify-center gap-4 pt-4 sm:gap-6">
          {TRUST_ICON_KEYS.map(({ icon: Icon, key }) => (
            <div
              key={key}
              className="flex items-center gap-2 rounded-full border border-primary-foreground/10 bg-primary-foreground/5 px-4 py-2"
            >
              <Icon className="h-4 w-4 text-secondary" strokeWidth={1.5} />
              <span className="text-xs font-medium text-primary-100 sm:text-sm">
                {t(key)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
